/**
 * Pre-submit insufficient-funds gate for the school checkout. Pure +
 * testable (no React, no `@/api` runtime imports) — mirrors `tax.ts`.
 *
 * Two backend contracts are mirrored EXACTLY so the gate's decision is
 * the decision the submit would make:
 *
 *   1. The debit amount — `computeSchoolOrderTotals` in
 *      `server/src/managers/school-order-submit.ts`: per line,
 *      `subtotal = unitPriceCents × quantity`, plus tax at the
 *      SCHOOL_FORECAST effective rate (0 today — the same `lineTaxCents`
 *      the summary's Tax row uses, so the two can never disagree). One
 *      cart line becomes one OrderItem at submit, so client and server
 *      round at the same granularity. No discount term: the account's
 *      early-payment incentive is a load-up wallet credit
 *      (`creditTopUpIncentive`), not an order-time subtraction.
 *
 *   2. The refusal rule — `debitAccount` in
 *      `server/src/managers/account-ledger.ts` (SQL): PREPAID needs
 *      `balance >= amount`; INVOICED may go negative within
 *      `creditLimitCents` (`balance − amount >= −creditLimit`), with
 *      NULL = unlimited (never refused). The gate fires ONLY when that
 *      SQL would refuse — accounts with unlimited or sufficient credit
 *      headroom are never gated.
 *
 * The gate is a UX affordance, not the enforcer: callers fail OPEN
 * (render the normal submit button) while the balance read is loading
 * or errored — the server's 402 stays the enforcer of record.
 */

import { smallestPresetAtLeast } from '@ordo/constants/wallet'

import type { SchoolCartLine } from '@/lib/school-orders/cart'
import { lineTaxCents } from '@/lib/school-orders/tax'

/** The account fields the gate decision needs (see `SchoolBillingAccount`). */
export interface SchoolFundsAccountView {
  balanceCents: number
  /** NULL = no explicit Prepaid/Invoiced selection yet (see below). */
  billingMode: 'PREPAID' | 'INVOICED' | null
  creditLimitCents: number | null
  earlyPaymentDiscountBps: number | null
}

/**
 * The load-up incentive rate the account's top-ups earn — the qualifying
 * rule from `creditTopUpIncentive` (server): the paying ORGANIZATION
 * account funds orders at order time (PREPAID). A negotiated rate stays
 * dormant while the account is INVOICED. Display-only on the client;
 * orders themselves charge full price.
 */
export function effectiveTopUpIncentiveBps(
  account: Pick<SchoolFundsAccountView, 'billingMode' | 'earlyPaymentDiscountBps'>,
): number {
  return account.billingMode === 'PREPAID' ? (account.earlyPaymentDiscountBps ?? 0) : 0
}

/**
 * The exact cents a full-cart submit debits the account: per-line
 * `subtotal + tax`, summed over priced lines. Returns `null` when NO
 * line carries a price (mirrors `subtotalCents` — the gate then fails
 * open; such a cart 422s server-side anyway).
 */
export function schoolOrderExpectedDebitCents(
  lines: readonly SchoolCartLine[],
  effectiveTaxRateBps: number,
): number | null {
  let total = 0
  let priced = false
  for (const l of lines) {
    if (l.unitPriceCents == null) {
      continue
    }
    priced = true
    const lineSubtotal = l.unitPriceCents * l.quantity
    total += lineSubtotal + lineTaxCents(lineSubtotal, effectiveTaxRateBps)
  }
  return priced ? total : null
}

/**
 * Gate decision. `gated: true` iff the ledger's debit SQL would refuse
 * this exact amount; `shortfallCents` is then the MINIMUM top-up that
 * makes the order placeable (debit − available headroom) — the number
 * behind "Minimum to place order is $X".
 */
export type SchoolFundsGate = { gated: false } | { gated: true; shortfallCents: number }

export function schoolFundsGate(account: SchoolFundsAccountView, expectedDebitCents: number): SchoolFundsGate {
  // Unset billing mode: the ledger refuses EVERY debit, and no top-up
  // amount fixes it — so the "minimum to place order" gate has nothing
  // truthful to say. Fail OPEN (the header's rule for states the gate
  // can't price) and let the server's 402 `billing_mode_unset` carry
  // the real provisioning message.
  if (account.billingMode === null) {
    return { gated: false }
  }
  // INVOICED with no credit limit: the ledger never refuses — never gate.
  if (account.billingMode === 'INVOICED' && account.creditLimitCents === null) {
    return { gated: false }
  }
  // Headroom the debit SQL allows: a PREPAID balance, or an INVOICED
  // balance plus its finite credit line. The SQL refuses strictly past
  // it (`>=` keeps an exact-headroom debit allowed), so gated ⇔ shortfall > 0.
  const headroomCents =
    account.billingMode === 'PREPAID' ? account.balanceCents : account.balanceCents + (account.creditLimitCents ?? 0)
  const shortfallCents = expectedDebitCents - headroomCents
  return shortfallCents > 0 ? { gated: true, shortfallCents } : { gated: false }
}

/**
 * Top-up sheet prefill: the smallest quick amount that covers the
 * shortfall; a shortfall past the largest quick amount prefills the exact
 * amount rounded UP to whole dollars. Always a whole-dollar cents value.
 *
 * `minTopUpCents` is the account's minimum load-up (PROD-1317) — pass the
 * same value the sheet holds, or the prefill can be an amount the top-up
 * route 400s only once the parent has hit submit. It cannot undershoot:
 * every offered quick amount already clears the minimum, and the
 * rounded-up branch is reached only for a shortfall larger than all of
 * them, therefore larger than the minimum too. Omit it and the platform
 * default applies, which is what non-family wallets run on.
 */
export function topUpPrefillCents(shortfallCents: number, minTopUpCents?: number): number {
  return smallestPresetAtLeast(shortfallCents, minTopUpCents) ?? Math.ceil(shortfallCents / 100) * 100
}
